import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { LockupLogo, BrandAsset } from "@/design-system";
import { AUTH, NAV } from "@/constants/testIds";
import { CheckSquare, MapPin, Users, Rss, UserCog, Radar, Settings, BarChart3, Menu, X, Wrench } from "lucide-react";

const NAV_ITEMS = [
  { to: "/admin/queue",     label: "Review Queue",         Icon: CheckSquare, testId: NAV.linkQueue },
  { to: "/admin/venues",    label: "Venues",               Icon: MapPin,      testId: NAV.linkVenues },
  { to: "/admin/organisers", label: "curators",          Icon: Users,       testId: NAV.linkLeads },
  { to: "/admin/published", label: "Published",            Icon: Rss,         testId: NAV.linkPublished },
  { to: "/admin/users",     label: "watu and roles",       Icon: UserCog,     testId: NAV.linkUsers },
  { to: "/admin/sources",   label: "Sources · pre-check",  Icon: Radar,       testId: NAV.linkSources },
  { to: "/admin/analytics", label: "Analytics",            Icon: BarChart3,   testId: "nav-link-analytics" },
  { to: "/admin/ops",       label: "Ops",                  Icon: Wrench,      testId: "nav-link-ops" },
  { to: "/admin/settings",  label: "Settings",             Icon: Settings,    testId: NAV.linkSettings },
];

const brandCase = (s) => (s || "").replace(/watu\s*wako/gi, "watu wako");
const firstName = (u) => {
  const raw = (u?.display_name || u?.name || (u?.email || "").split("@")[0] || "there") + "";
  return brandCase(raw.trim().split(/\s+/)[0]).toLowerCase();
};

// WW-149 · phones (<768) get the same menu as a drawer from a top bar button.
function MobileDeskMenu() {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="md:hidden">
      <div data-testid="admin-mobile-topbar" className="sticky top-0 z-50 flex h-12 items-center gap-3 border-b border-neutral-200 bg-white px-3">
        <button type="button" data-testid="admin-mobile-menu-button" aria-label="open menu" aria-expanded={open}
                onClick={() => setOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-neutral-100">
          <Menu size={20} />
        </button>
        <span className="text-sm font-semibold lowercase" style={{ fontFamily: "Poppins" }}>watu wako desk</span>
      </div>
      {open && (
        <div data-testid="admin-mobile-drawer-scrim" className="fixed inset-0 z-[60] bg-black/40" onClick={() => setOpen(false)}>
          <nav data-testid="admin-mobile-drawer" aria-label="admin menu" onClick={(e) => e.stopPropagation()}
               className="absolute left-0 top-0 flex h-full w-64 max-w-[85vw] flex-col gap-1 overflow-y-auto bg-white p-3 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.24em] text-neutral-500">admin desk</span>
              <button type="button" data-testid="admin-mobile-drawer-close" aria-label="close" onClick={() => setOpen(false)}
                      className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-neutral-100"><X size={18} /></button>
            </div>
            {NAV_ITEMS.map(({ to, label, Icon, testId }) => (
              <NavLink key={to} to={to} data-testid={`mobile-${testId}`} onClick={() => setOpen(false)}
                       className={({ isActive }) => `flex min-h-[44px] items-center gap-3 rounded-md px-3 text-sm font-medium ${isActive ? "bg-neutral-900 text-[#FDFBF7]" : "text-neutral-700 hover:bg-neutral-100"}`}>
                <Icon size={18} /> <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  // Msg 234 (30 Sep evening) · collapsed rail by default; hover expands.
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login", { replace: true });
    } else if (!loading && user && !user.is_admin) {
      navigate("/admin/login", { replace: true, state: { forbidden: true } });
    }
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]">
        <p className="label-tiny">Loading…</p>
      </div>
    );
  }

  const expanded = hovered;

  return (
    <div className="admin-force-light flex min-h-screen bg-[#FDFBF7]">
      {/* WW-148 (Claude QA 13 Oct · round 5 hotfix) · sticky elements
          form their own stacking context, so the aside's z-40 only
          applied inside the wrapper and positioned elements in <main>
          painted over the open 256 px panel.  z-50 on the sticky
          wrapper lifts the whole stacking context above anything in
          main; the aside keeps its own z-40 for ordering inside. */}
      <div className="sticky top-0 z-50 hidden shrink-0 self-start md:block" style={{ width: 64, height: "100vh" }}>
        <aside
          data-testid={NAV.sidebar}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="absolute left-0 top-0 z-40 flex h-full flex-col overflow-y-auto border-r border-neutral-200 bg-white transition-[width] duration-200"
          style={{
            width: expanded ? 256 : 64,
            boxShadow: expanded ? "0 10px 30px rgba(18,18,42,0.08)" : "none",
          }}>
          <div style={{ padding: "12px 12px 8px" }}>
            {/* WW-139 (Vania 11-12 Oct · Claude QA 2) · side menu stays
                WHITE.  Logo must be fully inside the menu, never cropped,
                at the brand's delivered aspect ratios (horizontal =
                1.744:1, mark = 0.887:1 — the manifest's old "4:1 /
                1:1" guides were wrong).  White ground → "ink type"
                variant per brand rule (LockupLogo ground="cream" maps to
                logo.horizontal.cream = fullcolour-ink-type).  Collapsed
                rail shows the full-colour mark (never on a coloured fill
                — the previous yellow "w" square is gone).  Header slot
                height is 112px when open so the 170x97 lockup clears the
                top with 12px breathing room; collapsed rail is 56px so
                the 40x45 mark centres with 12/13px above and below. */}
            <div className="flex overflow-hidden"
                 data-testid="admin-sidebar-logo"
                 style={{ height: expanded ? 112 : 56,
                          alignItems: expanded ? "flex-start" : "center",
                          justifyContent: expanded ? "flex-start" : "center",
                          paddingTop: expanded ? 12 : 0 }}>
              {expanded ? (
                <LockupLogo width={170} ground="cream" testId="admin-lockup" />
              ) : (
                <BrandAsset slot="logo.mark.512"
                            width={40} height={45}
                            alt="watu wako"
                            style={{ width: 40, height: 45 }}
                            testId="admin-lockup-mark" />
              )}
            </div>
            {expanded && (
              <>
                <p className="mt-2 text-xs uppercase tracking-[0.24em]" style={{ color: "var(--text-muted-cream)" }}>admin desk</p>
                <p data-testid="admin-hello" className="mt-3 text-sm font-medium"
                   style={{ color: "#1A1A30", fontFamily: "Poppins", textTransform: "lowercase" }}>
                  hello, {firstName(user)}
                </p>
              </>
            )}
          </div>

          <nav className="mt-2 flex flex-col gap-1 px-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.Icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  title={item.label}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors ${
                      isActive ? "bg-neutral-900 text-[#FDFBF7]" : "text-neutral-700 hover:bg-neutral-100"
                    }`
                  }>
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  {expanded && <span className="whitespace-nowrap">{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>

          {expanded && (
            <>
              <div className="mt-8 border-t border-neutral-200 px-3 pt-6 text-xs text-neutral-500">
                <p className="label-tiny mb-2">Public surfaces</p>
                <NavLink data-testid={NAV.linkSubmit} className="block py-1 hover:underline" to="/submit">Self-submission form</NavLink>
                <NavLink data-testid={NAV.linkPreview} className="block py-1 hover:underline" to="/embed-preview">Public grid preview</NavLink>
              </div>
              <div className="mt-auto border-t border-neutral-200 p-3 pt-4">
                <p className="truncate text-sm font-semibold">{brandCase(user.name || user.email)}</p>
                <p className="truncate text-xs text-neutral-500">{user.email}</p>
                <button
                  data-testid={AUTH.logoutButton}
                  onClick={logout}
                  className="mt-3 w-full rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-neutral-100">
                  Sign out
                </button>
              </div>
            </>
          )}
        </aside>
      </div>

      <main data-testid="admin-main-scroll" className="flex min-h-screen min-w-0 flex-1 flex-col">
        <MobileDeskMenu />
        <Outlet />
      </main>
    </div>
  );
}
